import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProgressRing from '../components/ProgressRing';
import CategoryChart from '../components/CategoryChart';
import useQuestionStore from '../stores/questionStore';
import useProgressStore from '../stores/progressStore';

// ── Backup & Restore ────────────────────────────────────────────────────
function DataBackup() {
  const exportProgress = useQuestionStore((s) => s.exportProgress);
  const importProgress = useQuestionStore((s) => s.importProgress);
  const stats = useQuestionStore((s) => s.getStats());
  const fileRef = useRef(null);
  const [status, setStatus] = useState(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await importProgress(file);
      setStatus({ type: 'success', msg: `Restored ${result.answersRestored} answers, ${result.flagsRestored} flags, ${result.confidenceRestored} confidence ratings. Reload to apply fully.` });
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      setStatus({ type: 'error', msg: err.message || 'Failed to restore backup' });
    }
    e.target.value = '';
  };

  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-1">Backup & Restore</h3>
      <p className="text-xs text-slate-500 mb-4">Save your progress locally so you never lose it — even if you clear your browser.</p>
      <div className="flex flex-wrap gap-3">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={exportProgress}
          className="px-4 py-2.5 bg-teal-500/15 border border-teal-500/30 text-teal-400 text-sm font-semibold rounded-lg hover:bg-teal-500/25 transition-colors"
        >
          💾 Export Progress
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => fileRef.current?.click()}
          className="px-4 py-2.5 bg-slate-700 border border-slate-600 text-slate-300 text-sm font-semibold rounded-lg hover:bg-slate-600 transition-colors"
        >
          📂 Restore from Backup
        </motion.button>
        <input ref={fileRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
      </div>
      <p className="text-xs text-slate-600 mt-3">{stats.attempted} answers • {Object.keys(useQuestionStore.getState().confidenceRatings).length} confidence ratings saved</p>
      {status && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-xs mt-2 ${status.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}
        >
          {status.msg}
        </motion.p>
      )}
    </div>
  );
}

// ── Boards Countdown ─────────────────────────────────────────────────────
function BoardsCountdown() {
  const { examDate, setExamDate, getDaysUntilExam, getDailyTargetToFinish } = useProgressStore();
  const questions = useQuestionStore((s) => s.questions);
  const stats = useQuestionStore((s) => s.getStats());

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(examDate || '');

  const days = getDaysUntilExam();
  const dailyTarget = getDailyTargetToFinish(questions.length, stats.attempted);

  const save = () => {
    if (draft) { setExamDate(draft); setEditing(false); }
  };

  if (!examDate || editing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-teal-900/60 to-slate-800 border border-teal-500/30 rounded-xl p-5"
      >
        <p className="text-sm font-semibold text-teal-300 mb-1">📅 Set Your Boards Date</p>
        <p className="text-xs text-slate-400 mb-3">I'll calculate your daily question target to finish before exam day.</p>
        <div className="flex gap-2">
          <input
            type="date"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="flex-1 bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500"
          />
          <button
            onClick={save}
            disabled={!draft}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-lg disabled:opacity-40 transition-colors"
          >
            Set
          </button>
          {editing && (
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-2 bg-slate-700 text-slate-400 text-sm rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  const formatted = new Date(examDate + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  const urgency = days <= 14 ? 'text-red-400 border-red-500/40 from-red-900/40'
    : days <= 30 ? 'text-amber-400 border-amber-500/40 from-amber-900/40'
    : 'text-teal-300 border-teal-500/30 from-teal-900/50';

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r ${urgency.split(' ')[2]} to-slate-800 border ${urgency.split(' ')[1]} rounded-xl p-5`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">ABE Written Boards</p>
          <div className="flex items-baseline gap-3">
            <span className={`text-6xl sm:text-7xl font-black tracking-tight ${urgency.split(' ')[0]} drop-shadow-lg`}>{days}</span>
            <div>
              <p className={`text-lg font-bold ${urgency.split(' ')[0]}`}>days away</p>
              <p className="text-xs text-slate-400">{formatted}</p>
            </div>
          </div>
        </div>
        <div className="text-right">
          {dailyTarget !== null && dailyTarget > 0 && (
            <div className="bg-slate-800/70 border border-slate-700 rounded-lg px-4 py-3">
              <p className="text-2xl font-bold text-white">{dailyTarget}</p>
              <p className="text-xs text-slate-400">questions/day</p>
              <p className="text-xs text-slate-500">to finish bank</p>
            </div>
          )}
          {dailyTarget === 0 && (
            <div className="bg-emerald-900/40 border border-emerald-500/30 rounded-lg px-4 py-3">
              <p className="text-emerald-400 font-semibold text-sm">All done! 🎉</p>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={() => { setDraft(examDate); setEditing(true); }}
        className="text-xs text-slate-500 hover:text-slate-300 mt-2 transition-colors"
      >
        Change date
      </button>
    </motion.div>
  );
}

// ── Question of the Day ──────────────────────────────────────────────────
function QuestionOfTheDay({ questions }) {
  const today = new Date().toISOString().slice(0, 10);
  const seed = today.split('-').reduce((a, n) => a + parseInt(n), 0);
  const idx = questions.length > 0 ? seed % questions.length : 0;
  const q = questions[idx];

  if (!q) return null;

  const diffColor = q.difficulty === 'Hard' ? 'text-red-400 bg-red-500/10 border-red-500/20'
    : q.difficulty === 'Medium' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
    : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';

  const choices = q.choices ?? q.options ?? [];
  const firstChoice = choices[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-slate-800/80 border border-teal-500/20 rounded-xl p-5 hover:border-teal-500/40 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-teal-400 uppercase tracking-wider">Question of the Day</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded-full">{q.category}</span>
          <span className={`text-xs border px-2 py-0.5 rounded-full ${diffColor}`}>{q.difficulty}</span>
        </div>
      </div>
      <p className="text-sm text-slate-200 leading-relaxed line-clamp-3">{q.question}</p>
      {firstChoice && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <span className="w-5 h-5 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-[10px] font-bold text-slate-400">A</span>
          <span className="truncate">{firstChoice}</span>
          <span className="text-slate-600 ml-1">...</span>
        </div>
      )}
      <Link to="/practice" className="inline-flex items-center gap-1.5 mt-3 text-xs text-teal-400 hover:text-teal-300 font-semibold transition-colors group">
        Answer this question
        <span className="group-hover:translate-x-0.5 transition-transform">&rarr;</span>
      </Link>
    </motion.div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function Dashboard() {
  const stats = useQuestionStore((s) => s.getStats());
  const questions = useQuestionStore((s) => s.questions);
  const userAnswers = useQuestionStore((s) => s.userAnswers);
  const confidenceStats = useQuestionStore((s) => s.getConfidenceStats());
  const { streak, dailyGoal } = useProgressStore();
  const todayProgress = useProgressStore((s) => s.getTodayProgress());
  const recentActivities = useProgressStore((s) => s.getRecentActivities(5));

  const dailyPct = dailyGoal > 0 ? Math.min(100, Math.round((todayProgress.questionsAnswered / dailyGoal) * 100)) : 0;

  // Unseen questions
  const unseenCount = useMemo(() => {
    return questions.filter(q => !userAnswers[q.id]).length;
  }, [questions, userAnswers]);

  // Weak count (unsure + kinda)
  const weakCount = confidenceStats.unsure + confidenceStats.kinda;

  // Weak categories (mastery < 75%, min 5 questions attempted)
  const weakCategories = useMemo(() => {
    return Object.entries(stats.byCategory)
      .filter(([, v]) => v.answered >= 5 && v.mastery < 75)
      .length;
  }, [stats.byCategory]);

  // Missed questions count
  const missedCount = useMemo(() => {
    return Object.values(userAnswers).filter(a => !a.isCorrect).length;
  }, [userAnswers]);

  const categoryData = Object.entries(stats.byCategory)
    .filter(([, v]) => v.total > 0)
    .map(([category, v]) => ({ category, correct: v.correct, total: v.answered || v.total }));

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{getGreeting()}, Doctor</h1>
          <p className="text-slate-400 text-sm mt-1">ABE Written Board Prep — keep pushing forward.</p>
        </div>
        <span className="text-xs text-teal-400 bg-teal-500/10 border border-teal-500/30 px-2.5 py-1 rounded-full font-semibold shrink-0">Beta</span>
      </div>

      {/* ── Keep Studying hero ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-teal-900/60 via-slate-800 to-slate-800 border border-teal-500/30 rounded-2xl p-6"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">Ready to study?</h2>
            <div className="flex flex-wrap gap-3 mt-2 text-sm">
              <span className="text-slate-400">{unseenCount} unseen</span>
              {weakCount > 0 && (
                <span className="text-red-400">{weakCount} need review</span>
              )}
              {missedCount > 0 && (
                <span className="text-rose-400">{missedCount} missed</span>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Link to="/practice" className="w-full sm:w-auto">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="w-full sm:w-auto px-8 py-3.5 bg-teal-500 hover:bg-teal-400 text-white font-bold text-lg rounded-xl shadow-lg shadow-teal-500/20 transition-colors"
              >
                Keep Studying →
              </motion.button>
            </Link>
            {weakCount > 0 && (
              <Link to="/practice?filter=weak" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full sm:w-auto px-6 py-3.5 bg-red-500/15 border-2 border-red-500/40 text-red-400 font-semibold rounded-xl hover:bg-red-500/25 transition-colors"
                >
                  Drill Weak ({weakCount})
                </motion.button>
              </Link>
            )}
          </div>
        </div>
      </motion.div>

      {/* Boards Countdown */}
      <BoardsCountdown />

      {/* Top Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Questions', value: stats.total, sub: `${stats.attempted} attempted` },
          { label: 'Accuracy', value: `${stats.percentCorrect}%`, sub: `${stats.correct}/${stats.attempted} correct` },
          { label: 'Streak', value: `${streak}🔥`, sub: 'day streak' },
          { label: 'Today', value: todayProgress.questionsAnswered, sub: `of ${dailyGoal} goal` },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="card p-4"
          >
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{stat.label}</p>
            <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{stat.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Confidence breakdown (compact) */}
      {(confidenceStats.know + confidenceStats.kinda + confidenceStats.unsure) > 0 && (
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Confidence Breakdown</h3>
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-400">{confidenceStats.know}</p>
              <p className="text-xs text-slate-500">Know it</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-amber-400">{confidenceStats.kinda}</p>
              <p className="text-xs text-slate-500">Kinda</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-red-400">{confidenceStats.unsure}</p>
              <p className="text-xs text-slate-500">Don't Know</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-400">{confidenceStats.unrated}</p>
              <p className="text-xs text-slate-500">Unrated</p>
            </div>
          </div>
          {/* Confidence bar */}
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden mt-3 flex">
            {confidenceStats.know > 0 && (
              <div className="bg-emerald-500 h-full" style={{ width: `${(confidenceStats.know / stats.total) * 100}%` }} />
            )}
            {confidenceStats.kinda > 0 && (
              <div className="bg-amber-500 h-full" style={{ width: `${(confidenceStats.kinda / stats.total) * 100}%` }} />
            )}
            {confidenceStats.unsure > 0 && (
              <div className="bg-red-500 h-full" style={{ width: `${(confidenceStats.unsure / stats.total) * 100}%` }} />
            )}
          </div>
        </div>
      )}

      {/* Smart Study */}
      <div>
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">⚡ Smart Study</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Fix Weaknesses */}
          <Link to="/practice?mode=weak">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-slate-800 border border-amber-500/25 hover:border-amber-500/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.08)] rounded-xl p-5 cursor-pointer transition-all group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-bold text-white group-hover:text-amber-300 transition-colors">⚡ Fix Weaknesses</p>
                  <p className="text-xs text-slate-400 mt-1">Auto-drill categories under 75% mastery</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${weakCategories > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-500'}`}>
                  {weakCategories > 0 ? `${weakCategories} weak` : 'all good'}
                </span>
              </div>
              <p className="text-xs text-amber-400/70 mt-3 font-medium">Start drilling →</p>
            </motion.div>
          </Link>

          {/* Review Missed */}
          <Link to="/practice?mode=missed">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-slate-800 border border-rose-500/25 hover:border-rose-500/50 hover:shadow-[0_0_15px_rgba(244,63,94,0.08)] rounded-xl p-5 cursor-pointer transition-all group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-bold text-white group-hover:text-rose-300 transition-colors">🔁 Review Missed</p>
                  <p className="text-xs text-slate-400 mt-1">Re-drill every question you got wrong</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${missedCount > 0 ? 'bg-rose-500/20 text-rose-400' : 'bg-slate-700 text-slate-500'}`}>
                  {missedCount > 0 ? `${missedCount} missed` : '0 missed'}
                </span>
              </div>
              <p className="text-xs text-rose-400/70 mt-3 font-medium">Start reviewing →</p>
            </motion.div>
          </Link>
        </div>
      </div>

      {/* Question of the Day */}
      {questions.length > 0 && <QuestionOfTheDay questions={questions} />}

      {/* Progress + Category Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 flex flex-col items-center gap-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider self-start">Daily Progress</h3>
          <ProgressRing progress={dailyPct} size={140} label={`${todayProgress.questionsAnswered}/${dailyGoal} questions`} />
          {todayProgress.questionsAnswered > 0 && (
            <p className="text-xs text-slate-400">
              {todayProgress.correctAnswers}/{todayProgress.questionsAnswered} correct today ({Math.round((todayProgress.correctAnswers / todayProgress.questionsAnswered) * 100)}%)
            </p>
          )}
        </div>
        <CategoryChart data={categoryData} title="Category Performance" />
      </div>

      {/* Recent Activity */}
      {recentActivities.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">Recent Activity</h3>
          <div className="space-y-2">
            {recentActivities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-sm">
                <span className="text-xs text-slate-500 font-mono w-20 shrink-0">
                  {new Date(a.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className={`w-2 h-2 rounded-full shrink-0 ${a.type === 'practice' ? 'bg-teal-400' : 'bg-amber-400'}`} />
                <span className="text-slate-300 truncate">{a.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backup & Restore */}
      <DataBackup />
    </div>
  );
}
